// T050 — Shimmer placeholders that match the post-load layout. Beats
// "Loading…" text by avoiding the layout shift + giving the operator a
// sense of progress. Uses Tailwind's `animate-pulse` + the theme's
// surface-2 colour so it adopts every theme.

export function SkeletonLine({ w = 'w-full', h = 'h-3', className = '' }) {
  return (
    <div className={`${w} ${h} rounded bg-slate-800 animate-pulse ${className}`} />
  );
}

// Row-shaped skeleton matching the dashboard table cells. Renders N rows
// inside a table-like container so the layout doesn't shift when real
// rows arrive.
export function TableSkeleton({ rows = 4 }) {
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-slate-800">
      <div className="space-y-0 divide-y divide-slate-800">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-[2fr_1fr_1fr_60px_1fr]">
            <div className="space-y-1.5">
              <SkeletonLine w="w-3/4" h="h-4" />
              <SkeletonLine w="w-1/2" h="h-2" />
            </div>
            <SkeletonLine w="w-3/4" h="h-3" />
            <SkeletonLine w="w-2/3" h="h-3" />
            <SkeletonLine w="w-10" h="h-6" className="ml-auto rounded" />
            <SkeletonLine w="w-32" h="h-6" className="ml-auto rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Big form section skeleton — for ProfilePage's initial load. Matches the
// two-column field layout so the skeleton sits in the same boxes as the
// real fields.
export function FormSkeleton({ sections = 4 }) {
  return (
    <div className="space-y-8">
      {Array.from({ length: sections }).map((_, i) => (
        <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/30 p-5">
          <SkeletonLine w="w-32" h="h-4" />
          <div className="mt-4 space-y-3">
            <SkeletonLine w="w-full" h="h-9" className="rounded-md" />
            <SkeletonLine w="w-full" h="h-9" className="rounded-md" />
            <div className="grid gap-3 sm:grid-cols-2">
              <SkeletonLine w="w-full" h="h-9" className="rounded-md" />
              <SkeletonLine w="w-full" h="h-9" className="rounded-md" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Stat-card grid skeleton — matches the 5-column dashboard stat tiles.
export function StatGridSkeleton({ count = 5 }) {
  return (
    <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <SkeletonLine w="w-20" h="h-2" />
          <SkeletonLine w="w-16" h="h-7" className="mt-2" />
          <SkeletonLine w="w-24" h="h-2" className="mt-2" />
        </div>
      ))}
    </section>
  );
}
